import React, { useState } from 'react';
import { Download, Smartphone, Check } from 'lucide-react';
import { useInstallPrompt } from '../lib/installPrompt';

/**
 * InstallAppCard
 * ─────────────────────────────────────────────────────────────
 * Two-column layout:
 *   Android:  one big "Uygulamayı Yükle" button (native install dialog)
 *   iPhone:   clear 3-step explanation (no programmatic install — Safari
 *             does not expose beforeinstallprompt)
 *
 * Browsers cannot auto-install a PWA without an explicit user gesture
 * (Chrome security requirement), so "install" is always one tap.
 *
 * The card hides itself completely once the app is running in
 * display-mode: standalone (already installed).
 */
interface InstallAppCardProps {
  className?: string;
}

const InstallAppCard: React.FC<InstallAppCardProps> = ({ className = '' }) => {
  const { canInstall, isStandalone, promptInstall, installed } = useInstallPrompt();
  const [busy, setBusy] = useState(false);
  const [justInstalled, setJustInstalled] = useState(false);

  // Already running as a PWA → hide entirely.
  if (isStandalone) return null;

  const onInstall = async () => {
    setBusy(true);
    try {
      const r = await promptInstall();
      if (r.outcome === 'accepted') setJustInstalled(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="install-app-card"
      className={`rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden ${className}`}
    >
      <div className="p-4 flex items-start gap-3 border-b border-stone-100">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-[#6b0f1a]/10 text-[#6b0f1a] flex items-center justify-center">
          <Smartphone size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm text-stone-900" data-testid="install-app-title">
            Uygulamayı telefonunuza ekleyin
          </h4>
          <p className="text-xs text-stone-600 mt-1 leading-relaxed">
            Daha hızlı erişim ve bildirimler için paneli tam ekran uygulama olarak kullanın.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-stone-100">
        {/* ─────── Android: one tap ─────── */}
        <section className="p-4" data-testid="install-steps-android">
          <h5 className="font-semibold text-xs text-stone-900 mb-2 flex items-center gap-1.5 uppercase tracking-wide">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#0F9D58]" />
            Android
          </h5>

          {installed || justInstalled ? (
            <div
              data-testid="install-app-installed"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200"
            >
              <Check size={14} /> Yüklendi
            </div>
          ) : canInstall ? (
            <button
              onClick={onInstall}
              disabled={busy}
              data-testid="install-app-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg bg-[#6b0f1a] text-white hover:bg-[#8a1422] disabled:opacity-60 transition-colors shadow-sm"
            >
              <Download size={16} />
              {busy ? 'Yükleniyor…' : 'Uygulamayı Yükle'}
            </button>
          ) : (
            <div className="text-xs text-stone-600 leading-relaxed" data-testid="install-app-android-hint">
              <p>Chrome/Edge'de sağ üstteki <strong className="text-stone-900">⋮</strong> menüden <strong className="text-stone-900">"Uygulamayı yükle"</strong>'ye dokunun.</p>
              <p className="mt-1.5 text-[11px] text-stone-400">
                Bazen tarayıcı bir kaç saniye sonra otomatik "Yükle" butonu gösterebilir.
              </p>
            </div>
          )}
        </section>

        {/* ─────── iPhone: explanation ─────── */}
        <section className="p-4" data-testid="install-steps-iphone">
          <h5 className="font-semibold text-xs text-stone-900 mb-2 flex items-center gap-1.5 uppercase tracking-wide">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#007AFF]" />
            iPhone / iPad
          </h5>
          <ol className="list-decimal pl-5 space-y-1 text-xs text-stone-600 leading-relaxed">
            <li>Paneli <strong className="text-stone-900">Safari</strong> ile açın.</li>
            <li>Alt çubuktaki <strong className="text-[#007AFF]">Paylaş</strong> (yukarı oklu kutu) simgesine dokunun.</li>
            <li><strong className="text-stone-900">"Ana Ekrana Ekle"</strong> → <strong>Ekle</strong>.</li>
          </ol>
        </section>
      </div>
    </div>
  );
};

export default InstallAppCard;
